import React, { useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  TableRow,
  Box,
  Typography,
  TableCell,
  IconButton,
  TableHead,
  Checkbox
} from "@mui/material";
import TableSortIcon from "./TableSortIcon";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  title: {
    color: "white",
    fontSize: "0.875rem"
  },
  cell: {
    background: theme.gradient,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    border: "none",
    height: theme.spacing(6),
    position: "sticky",
    top: "0",
    zIndex: "10",
    whiteSpace: "nowrap"
  },
  checkbox: {
    color: theme.palette.common.white,
    "&.Mui-checked": {
      color: theme.palette.common.white
    }
  },
  sortIcon: {
    marginLeft: theme.spacing(0.5)
  }
}));

function Header(props) {
  const { columnDef } = props;
  const columnHeaderRef = useRef(null);
  const { t } = useTranslation();
  const classes = useStyles();

  function getSortIcon(columnDef) {
    if (columnDef.options && columnDef.options.sorting) {
      return (
        <IconButton
          className={classes.sortIcon}
          size="small"
          onClick={() => {
            const orderDirection =
              columnDef.tableData.id !== props.orderBy
                ? "asc"
                : props.orderDirection === "asc"
                ? "desc"
                : "asc";
            props.onOrderChange(columnDef.tableData.id, orderDirection);
          }}
        >
          <TableSortIcon
            sortDirection={
              props.orderBy === columnDef.tableData.id
                ? props.orderDirection
                : ""
            }
          />
        </IconButton>
      );
    }
  }

  return (
    <TableCell
      size="small"
      key={columnDef.tableData.id}
      className={classes.cell}
      style={{
        ...props.headerStyle,
        ...columnDef.headerStyle
      }}
      ref={columnHeaderRef}
    >
      <Box display="flex" alignItems="center">
        <Typography classes={{ root: classes.title }} variant="subtitle2">
          {columnDef.title ? t("table.columnHeader." + columnDef.title) : ""}
        </Typography>
        {columnDef.headerCheckboxProps && (
          <Checkbox
            className={classes.checkbox}
            onChange={(event, checked) =>
              columnDef.headerCheckboxProps.onCheck(checked, columnDef)
            }
            style={{ marginLeft: 10 }}
            checked={columnDef.headerCheckboxProps.checked}
            disabled={columnDef.headerCheckboxProps.disabled}
          />
        )}
        {getSortIcon(columnDef)}
      </Box>
    </TableCell>
  );
}

function ColumnHeader(props) {
  const classes = useStyles();
  const tableHeadRef = useRef(null);

  function renderSelectionHeader() {
    const columnHeaderRef = useRef(null);

    return (
      <TableCell
        key="key-selection-column"
        padding="none"
        size="small"
        ref={columnHeaderRef}
        className={classes.cell}
        style={{
          ...props.selectionCellStyle,
          ...props.selectAllStyle
        }}
      >
        {props.showSelectAllCheckbox && (
          <Checkbox
            className={classes.checkbox}
            checked={props.selectAllChecked}
            onChange={(event, checked) =>
              props.onAllSelected && props.onAllSelected(checked)
            }
            style={{ marginLeft: 10 }}
          />
        )}
      </TableCell>
    );
  }

  const DetailColumn = props => {
    const columnHeaderRef = useRef(null);

    return (
      <TableCell
        key={"detail-column"}
        padding="none"
        size="small"
        ref={columnHeaderRef}
        className={classes.cell}
        style={{
          width: "40px"
        }}
      />
    );
  };

  function renderDragHeader() {
    const columnHeaderRef = useRef(null);

    return (
      <TableCell
        key="key-drag-column"
        padding="none"
        size="small"
        ref={columnHeaderRef}
        className={classes.cell}
        style={{
          width: "30px"
        }}
      >
        <div />
      </TableCell>
    );
  }

  let headers = props.columns.map((columnDef, index) => (
    <Header {...props} {...{ columnDef }} key={index} />
  ));

  if (props.hasDetailPanel) {
    const detailColumn = <DetailColumn />;
    if (props.options.detailPanelColumnAlignment === "right") {
      headers.push(detailColumn);
    } else {
      headers.splice(0, 0, detailColumn);
    }
  }

  if (props.hasSelection) {
    headers.splice(0, 0, renderSelectionHeader());
  }

  if (props.isDraggable) {
    headers.push(renderDragHeader());
  }
  return (
    <TableHead ref={tableHeadRef}>
      <TableRow>{headers}</TableRow>
    </TableHead>
  );
}

export default ColumnHeader;
